// Drum presets and bass line styles for the backing track.
//
// A drum pattern is one bar on a grid of `subdivision` steps per beat (4 =
// sixteenths, 3 = triplets). Each voice is a string with one character per
// step: '.' rest, 'o' soft, 'x' normal, 'X' accent.

export const DRUM_VOICES = ['kick', 'snare', 'rim', 'hat', 'open', 'shaker'];

export const VELOCITY = { '.': 0, 'o': 0.35, 'x': 0.7, 'X': 1 };
export const NEXT_VELOCITY = { '.': 'x', 'x': 'X', 'X': 'o', 'o': '.' };

export const PRESETS = [
    {
        id: 'rock', beats: 4, subdivision: 4, swing: 0, bpm: 110, bass: 'eighths', bassSound: 'electric',
        drums: {
            kick: 'X.......x.x.....',
            snare: '....X.......X...',
            hat: 'X.x.x.x.X.x.x.x.',
        },
    },
    {
        id: 'pop', beats: 4, subdivision: 4, swing: 0, bpm: 100, bass: 'pop', bassSound: 'electric',
        drums: {
            kick: 'X.....x.x.......',
            snare: '....X.......X...',
            hat: 'x.x.x.x.x.x.x.x.',
        },
    },
    {
        id: 'ballad', beats: 4, subdivision: 4, swing: 0, bpm: 70, bass: 'root', bassSound: 'electric',
        drums: {
            kick: 'X.........x.....',
            rim: '........x.......',
            hat: 'o.o.o.o.o.o.o.o.',
        },
    },
    {
        id: 'folk', beats: 4, subdivision: 4, swing: 0, bpm: 120, bass: 'rootFifth', bassSound: 'upright',
        drums: {
            kick: 'X.......x.......',
            snare: '....x.......x...',
            shaker: 'x.ox.oxox.ox.oxo',
        },
    },
    {
        id: 'shuffle', beats: 4, subdivision: 4, swing: 1, bpm: 100, bass: 'boogie', bassSound: 'electric',
        drums: {
            kick: 'X.......x.......',
            snare: '....X.......X...',
            hat: 'x.x.x.x.x.x.x.x.',
        },
    },
    {
        id: 'disco', beats: 4, subdivision: 4, swing: 0, bpm: 118, bass: 'octaves', bassSound: 'electric',
        drums: {
            kick: 'X...x...x...x...',
            snare: '....X.......X...',
            hat: 'x...x...x...x...',
            open: '..x...x...x...x.',
        },
    },
    {
        id: 'funk', beats: 4, subdivision: 4, swing: 0, bpm: 98, bass: 'funk', bassSound: 'electric',
        drums: {
            kick: 'X..x..x...x..x..',
            snare: '....X..o.o..X..o',
            hat: 'XxxxXxxxXxxxXxxx',
        },
    },
    {
        id: 'bossa', beats: 4, subdivision: 4, swing: 0, bpm: 130, bass: 'bossa', bassSound: 'upright',
        drums: {
            kick: 'x..xx..xx..xx..x',
            rim: 'x..x..x...x..x..',
            hat: 'oooooooooooooooo',
        },
    },
    {
        id: 'reggae', beats: 4, subdivision: 4, swing: 0.4, bpm: 76, bass: 'reggae', bassSound: 'electric',
        drums: {
            kick: '........X.......',
            rim: '........X.......',
            hat: 'x.x.x.x.x.x.x.x.',
        },
    },
    {
        id: 'waltz', beats: 3, subdivision: 4, swing: 0, bpm: 96, bass: 'waltz', bassSound: 'upright',
        drums: {
            kick: 'X...........',
            snare: '....o...o...',
            hat: 'x...x...x...',
        },
    },
    {
        id: 'slow68', beats: 4, subdivision: 3, swing: 0, bpm: 60, bass: 'arpeggio', bassSound: 'electric',
        drums: {
            kick: 'X.....x.....',
            snare: '...x.....x..',
            hat: 'xooxooxooxoo',
        },
    },
    {
        id: 'click', beats: 4, subdivision: 4, swing: 0, bpm: 90, bass: 'none', bassSound: 'electric',
        drums: {
            rim: 'X...x...x...x...',
        },
    },
];

// Display names live here (not in PRESETS) so gettext runs at render time.
export function presetName(id) {
    return {
        rock: gettext('Rock'),
        pop: gettext('Pop'),
        ballad: gettext('Ballad'),
        folk: gettext('Folk / country'),
        shuffle: gettext('Shuffle / blues'),
        disco: gettext('Disco'),
        funk: gettext('Funk'),
        bossa: gettext('Bossa nova'),
        reggae: gettext('Reggae'),
        waltz: gettext('Waltz (3/4)'),
        slow68: gettext('Slow 6/8'),
        click: gettext('Click only'),
    }[id] || id;
}

export function voiceName(voice) {
    return {
        kick: gettext('Kick'),
        snare: gettext('Snare'),
        rim: gettext('Rim'),
        hat: gettext('Hi-hat'),
        open: gettext('Open hat'),
        shaker: gettext('Shaker'),
    }[voice];
}

export function bassSoundName(id) {
    return {
        electric: gettext('Electric bass'),
        upright: gettext('Upright bass'),
        synth: gettext('Synth bass'),
    }[id] || id;
}

export function drumSoundName(id) {
    return {
        acoustic: gettext('Acoustic kit'),
        synth: gettext('Synth drums'),
    }[id] || id;
}

export function emptyGrid(preset) {
    const steps = preset.beats * preset.subdivision;
    const grid = {};
    DRUM_VOICES.forEach(voice => {
        grid[voice] = (preset.drums[voice] || '').padEnd(steps, '.').slice(0, steps);
    });
    return grid;
}

// Bass styles. Each returns the notes of one bar as { step, degree, length }
// (length in steps). Degrees: 'R' bass note of the chord (the slash note for
// "G/B"), '5' fifth, '8' root an octave up, '3' third, '6' sixth, 'b7'
// seventh, 'A' a chromatic approach to the next chord.
export const BASS_STYLES = ['root', 'rootFifth', 'eighths', 'pop', 'walking', 'octaves', 'boogie', 'funk', 'bossa', 'reggae', 'waltz', 'arpeggio', 'none'];

export function bassStyleName(id) {
    return {
        root: gettext('Root notes'),
        rootFifth: gettext('Root and fifth'),
        eighths: gettext('Driving eighths'),
        pop: gettext('Pop'),
        walking: gettext('Walking'),
        octaves: gettext('Octaves'),
        boogie: gettext('Boogie'),
        funk: gettext('Funk'),
        bossa: gettext('Bossa nova'),
        reggae: gettext('Reggae'),
        waltz: gettext('Oom-pah'),
        arpeggio: gettext('Arpeggio'),
        none: gettext('No bass'),
    }[id] || id;
}

export function bassBar(style, { beats, subdivision }, bar) {
    const beat = subdivision;
    const steps = beats * subdivision;
    const eighth = subdivision === 4 ? 2 : subdivision;
    const notes = [];
    const add = (step, degree, length) => {
        if (step < steps) notes.push({ step, degree, length: Math.min(length, steps - step) });
    };
    const half = beats % 2 === 0 ? (beats / 2) * beat : steps;

    switch (style) {
        case 'none':
            break;
        case 'root':
            add(0, 'R', half);
            if (half < steps) add(half, 'R', half);
            break;
        case 'rootFifth':
            if (beats === 3) {
                add(0, bar % 2 ? '5' : 'R', beat);
            } else {
                add(0, 'R', half);
                add(half, '5', half);
            }
            break;
        case 'eighths':
            for (let s = 0; s < steps; s += eighth) add(s, 'R', eighth);
            break;
        case 'octaves':
            for (let s = 0, i = 0; s < steps; s += eighth, i++) add(s, i % 2 ? '8' : 'R', eighth);
            break;
        case 'walking': {
            const line = beats === 3 ? ['R', '5', 'A'] : ['R', '3', '5', 'A'];
            for (let b = 0; b < beats; b++) add(b * beat, line[b % line.length], beat);
            break;
        }
        case 'boogie': {
            const line = bar % 2 ? ['b7', '6', '5', '3'] : ['R', '3', '5', '6'];
            for (let b = 0; b < beats; b++) {
                add(b * beat, line[b % 4], eighth);
                add(b * beat + eighth, line[b % 4], beat - eighth);
            }
            break;
        }
        case 'pop':
            if (subdivision !== 4 || beats !== 4) return bassBar('rootFifth', { beats, subdivision }, bar);
            add(0, 'R', 4); add(6, 'R', 2); add(8, '5', 4); add(12, 'R', 2); add(14, 'A', 2);
            break;
        case 'funk':
            if (subdivision !== 4 || beats !== 4) return bassBar('octaves', { beats, subdivision }, bar);
            add(0, 'R', 2); add(3, 'R', 1); add(6, '8', 1); add(8, 'R', 2); add(11, '5', 1); add(12, 'b7', 1); add(14, '8', 2);
            break;
        case 'bossa':
            if (subdivision !== 4 || beats !== 4) return bassBar('rootFifth', { beats, subdivision }, bar);
            add(0, 'R', 6); add(6, '5', 2); add(8, '5', 6); add(14, 'R', 2);
            break;
        case 'reggae':
            if (subdivision !== 4 || beats !== 4) return bassBar('root', { beats, subdivision }, bar);
            add(0, 'R', 4); add(6, '5', 2); add(8, '8', 3); add(12, '5', 2); add(14, 'R', 2);
            break;
        case 'waltz':
            add(0, bar % 2 ? '5' : 'R', beat);
            break;
        case 'arpeggio': {
            const line = ['R', '5', '8'];
            for (let s = 0, i = 0; s < steps; s += (subdivision === 3 ? 1 : 2), i++) add(s, line[i % 3], subdivision === 3 ? 1 : 2);
            break;
        }
        default:
            add(0, 'R', steps);
    }
    return notes;
}

const LOWEST_BASS_NOTE = 33; // A1: roots sit in A1–G#2, where a bass player would put them

// MIDI note for a bass degree over a chord.
export function bassMidi(degree, chord, nextChord) {
    const base = LOWEST_BASS_NOTE + ((chord.bass - LOWEST_BASS_NOTE % 12 + 12) % 12);
    const rootBase = LOWEST_BASS_NOTE + ((chord.root - LOWEST_BASS_NOTE % 12 + 12) % 12);
    switch (degree) {
        case 'R': return base;
        case '5': return rootBase + chord.fifth;
        case '8': return base + 12;
        case '3': return rootBase + chord.third;
        case '6': return rootBase + 9;
        case 'b7': return rootBase + (chord.intervals.includes(11) ? 11 : 10);
        case 'A': {
            if (!nextChord || nextChord.bass === chord.bass) return rootBase + chord.fifth;
            const target = LOWEST_BASS_NOTE + ((nextChord.bass - LOWEST_BASS_NOTE % 12 + 12) % 12);
            // Approach from a semitone below, or above when that's closer to where we are.
            return Math.abs(target + 1 - base) < Math.abs(target - 1 - base) ? target + 1 : target - 1;
        }
        default: return base;
    }
}
